import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { ToastrService } from 'ngx-toastr';
import {
  datetimeLocalToIso,
  isoToDatetimeLocal,
  offerPriceValidator,
  requiredQuantityValidator,
} from 'src/app/services/product-pricing.util';

interface DiscountGroupOption {
  discountGroup: string;
  productCount: number;
  productsWithOwnOffers?: number;
  activeOfferCount?: number;
}

@Component({
  selector: 'app-discount-group-offer-form-dialog',
  templateUrl: './discount-group-offer-form-dialog.component.html',
  styleUrls: ['./discount-group-offer-form-dialog.component.scss'],
})
export class DiscountGroupOfferFormDialogComponent implements OnInit, OnDestroy {
  @Input() storeId: string = '';
  @Input() offer: any = null;

  groupSearchInput$ = new Subject<string>();
  groupOptions: DiscountGroupOption[] = [];
  selectedGroup: DiscountGroupOption | null = null;
  groupsLoading = false;

  offerName = '';
  offerRequiredQuantity: number | null = null;
  offerPrice: number | null = null;
  offerValidFrom = '';
  offerValidTo = '';
  offerIsActive = true;

  offerQtyTouched = false;
  offerPriceTouched = false;
  groupTouched = false;
  offerInlineError = '';
  offerSaving = false;

  private destroy$ = new Subject<void>();
  private knownGroupKeys = new Set<string>();

  constructor(
    private dialogRef: NbDialogRef<DiscountGroupOfferFormDialogComponent>,
    private ecommerceService: EcommerceService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.setupGroupSearch();

    if (this.offer) {
      this.offerName = this.offer.name || '';
      this.offerRequiredQuantity = this.offer.requiredQuantity;
      this.offerPrice = this.offer.offerPrice;
      this.offerValidFrom = isoToDatetimeLocal(this.offer.validFrom);
      this.offerValidTo = isoToDatetimeLocal(this.offer.validTo);
      this.offerIsActive = this.offer.isActive !== false;
      this.loadGroups(this.offer.discountGroup || '', () => {
        const match = this.groupOptions.find(
          (g) => g.discountGroup === this.offer.discountGroup
        );
        if (match) {
          this.selectedGroup = match;
        } else if (this.offer.discountGroup) {
          this.selectedGroup = {
            discountGroup: this.offer.discountGroup,
            productCount: 0,
          };
          this.groupOptions = [this.selectedGroup, ...this.groupOptions];
        }
      });
    } else {
      this.loadGroups('');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isEdit(): boolean {
    return !!this.offer?._id;
  }

  get offerQtyError(): string | null {
    if (!this.offerQtyTouched) {
      return null;
    }
    return (
      requiredQuantityValidator({ value: this.offerRequiredQuantity } as any)?.['requiredQuantity']
        ?.message || null
    );
  }

  get offerPriceError(): string | null {
    if (!this.offerPriceTouched) {
      return null;
    }
    return offerPriceValidator({ value: this.offerPrice } as any)?.['offerPrice']?.message || null;
  }

  get groupError(): string | null {
    if (!this.groupTouched) {
      return null;
    }
    if (!this.selectedGroup?.discountGroup?.trim()) {
      return 'Discount group is required';
    }
    const key = this.selectedGroup.discountGroup.trim().toLowerCase();
    if (!this.isEdit && !this.knownGroupKeys.has(key)) {
      return 'Choose a discount group from the list (products must use this group label)';
    }
    return null;
  }

  canSubmitOffer(): boolean {
    const group = this.selectedGroup?.discountGroup?.trim();
    const baseValid =
      !requiredQuantityValidator({ value: this.offerRequiredQuantity } as any) &&
      !offerPriceValidator({ value: this.offerPrice } as any) &&
      !!group;
    if (!baseValid) {
      return false;
    }
    if (this.isEdit) {
      return true;
    }
    return this.knownGroupKeys.has(group!.toLowerCase());
  }

  formatGroupLabel(item: DiscountGroupOption): string {
    const count = item.productCount ?? 0;
    return `${item.discountGroup} (${count} product${count === 1 ? '' : 's'})`;
  }

  onGroupSelected(item: DiscountGroupOption | null): void {
    this.selectedGroup = item;
    this.groupTouched = true;
  }

  saveOffer(): void {
    this.offerQtyTouched = true;
    this.offerPriceTouched = true;
    this.groupTouched = true;
    this.offerInlineError = '';

    if (!this.canSubmitOffer()) {
      return;
    }

    const payload: any = {
      discountGroup: this.selectedGroup!.discountGroup.trim(),
      requiredQuantity: Number(this.offerRequiredQuantity),
      offerPrice: Number(this.offerPrice),
      isActive: this.offerIsActive,
    };

    const trimmedName = (this.offerName || '').trim();
    if (trimmedName) {
      payload.name = trimmedName;
    } else if (this.isEdit) {
      payload.name = '';
    }

    const validFromIso = datetimeLocalToIso(this.offerValidFrom);
    const validToIso = datetimeLocalToIso(this.offerValidTo);
    if (validFromIso) {
      payload.validFrom = validFromIso;
    } else if (this.isEdit) {
      payload.validFrom = null;
    }
    if (validToIso) {
      payload.validTo = validToIso;
    } else if (this.isEdit) {
      payload.validTo = null;
    }

    this.offerSaving = true;
    const request$ = this.isEdit
      ? this.ecommerceService.updateDiscountGroupOffer(this.storeId, this.offer._id, payload)
      : this.ecommerceService.createDiscountGroupOffer(this.storeId, payload);

    request$.subscribe({
      next: () => {
        this.offerSaving = false;
        this.toastr.success(this.isEdit ? 'Group offer updated' : 'Group offer created');
        this.dialogRef.close({ saved: true });
      },
      error: (err: any) => {
        this.offerSaving = false;
        this.offerInlineError = err?.error?.response?.message || 'Failed to save group offer';
      },
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private setupGroupSearch(): void {
    this.groupSearchInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          this.groupsLoading = true;
          return this.ecommerceService.getDiscountGroupsForStore(this.storeId, { q: term || '' });
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (res: any) => {
          this.groupsLoading = false;
          const data = res?.response?.data ?? res?.data;
          this.applyGroupItems(this.extractItems(data));
        },
        error: () => {
          this.groupsLoading = false;
        },
      });

    this.groupSearchInput$.next('');
  }

  private loadGroups(q: string, after?: () => void): void {
    this.groupsLoading = true;
    this.ecommerceService.getDiscountGroupsForStore(this.storeId, { q }).subscribe({
      next: (res: any) => {
        this.groupsLoading = false;
        const data = res?.response?.data ?? res?.data;
        this.applyGroupItems(this.extractItems(data));
        if (after) {
          after();
        }
      },
      error: () => {
        this.groupsLoading = false;
        if (after) {
          after();
        }
      },
    });
  }

  private applyGroupItems(items: DiscountGroupOption[]): void {
    this.groupOptions = items;
    this.knownGroupKeys = new Set(
      items.map((g) => (g.discountGroup || '').trim().toLowerCase()).filter(Boolean)
    );
  }

  private extractItems(data: any): DiscountGroupOption[] {
    if (Array.isArray(data)) {
      return data;
    }
    if (data?.items && Array.isArray(data.items)) {
      return data.items;
    }
    return [];
  }
}
